import { useLinks } from '../../hooks/useLinks'

interface LinkInputProps {
  linkHref?: string
  linkBadge?: string
  onChangeHref: (value: string | undefined) => void
  onChangeBadge: (value: string | undefined) => void
}

/**
 * Combined dropdown for selecting a link destination: either a badge reference
 * from the Links page (auto-synced) or a custom URL / internal path.
 */
export function LinkInput({ linkHref, linkBadge, onChangeHref, onChangeBadge }: LinkInputProps) {
  const { data: linksData } = useLinks()

  // Build list of badge options from the links API
  const badgeLinks = linksData?.data.links.filter((link) => link.badge) ?? []

  // Determine current selection mode
  const isCustomUrl = !linkBadge && !!linkHref
  const selectedValue = linkBadge ? `badge:${linkBadge}` : isCustomUrl ? 'custom' : ''

  // Resolve the displayed URL for current selection
  const resolvedUrl = linkBadge ? badgeLinks.find((l) => l.badge === linkBadge)?.href : linkHref

  const handleSelectChange = (value: string) => {
    if (value === 'custom') {
      onChangeBadge(undefined)
      onChangeHref(linkHref || '')
    } else if (value.startsWith('badge:')) {
      const badge = value.slice('badge:'.length)
      onChangeBadge(badge)
      onChangeHref(undefined)
    } else {
      // "none" — clear both
      onChangeBadge(undefined)
      onChangeHref(undefined)
    }
  }

  const truncateUrl = (url: string, maxLen = 35) =>
    url.length > maxLen ? url.slice(0, maxLen) + '...' : url

  const badgeEmoji: Record<string, string> = {
    official: '\u2B50',
    video: '\uD83C\uDFA5',
    pdf: '\uD83D\uDCC4',
    internal: '\uD83D\uDD12',
    merch: '\uD83D\uDECD\uFE0F',
    instagram: '\uD83D\uDCF8',
    photos: '\uD83D\uDDBC\uFE0F',
  }

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">Link Destination</label>
      <select
        value={selectedValue}
        onChange={(e) => handleSelectChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rhs-maroon focus:border-rhs-maroon text-sm bg-white"
      >
        <option value="">No link override (uses fallback)</option>
        <option value="custom">Custom URL...</option>
        {badgeLinks.length > 0 && (
          <optgroup label="From Links Page">
            {badgeLinks.map((link) => (
              <option key={link.badge} value={`badge:${link.badge}`}>
                {badgeEmoji[link.badge!] || ''} {link.label} — {truncateUrl(link.href)}
              </option>
            ))}
          </optgroup>
        )}
      </select>

      {isCustomUrl && (
        <input
          type="text"
          value={linkHref || ''}
          onChange={(e) => onChangeHref(e.target.value)}
          placeholder='e.g. "/calendar" or "https://example.com"'
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rhs-maroon focus:border-rhs-maroon text-sm"
        />
      )}

      {resolvedUrl && <p className="text-xs text-gray-500 truncate">Resolves to: {resolvedUrl}</p>}
    </div>
  )
}
